import { createSupabaseClient, setSessionFromToken } from '@golfauth/auth';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  const errorMsg = 'Missing Supabase environment variables. Please check your configuration.';
  console.error(errorMsg, {
    url: supabaseUrl ? 'present' : 'MISSING - VITE_SUPABASE_URL not found',
    key: supabaseAnonKey ? 'present' : 'MISSING - VITE_SUPABASE_ANON_KEY not found'
  });
  throw new Error(errorMsg);
}

export const supabase = createSupabaseClient({
  url: supabaseUrl,
  anonKey: supabaseAnonKey,
  useSharedCookies: true,
});

/** Headers for browser fetch() to Edge Functions (gateway requires apikey + user JWT). */
export function edgeFunctionHeaders(accessToken: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    apikey: supabaseAnonKey,
  };
}

/**
 * Resolves after any `token` + `refresh_token` query handling (Core → Fitting).
 * Auth store must await this before `initializeAuth()` or protected routes see no session and redirect.
 */
export const supabaseAuthReady: Promise<void> =
  typeof window === 'undefined'
    ? Promise.resolve()
    : (async () => {
        // Cross-app tokens now arrive in the URL FRAGMENT (#…) rather
        // than the query string. Cloudflare's WAF was blocking apex
        // golfauth.com requests that carried long JWTs in ?token=…
        // (matched a credential-abuse rule). Fragments never reach the
        // server so the WAF has nothing to inspect. Query-string is
        // still checked as a fallback for older URLs that might be
        // bookmarked from before the switch.
        const hashParams = new URLSearchParams(
          window.location.hash.replace(/^#/, ''),
        );
        const urlParams = new URLSearchParams(window.location.search);
        const token = hashParams.get('token') ?? urlParams.get('token');
        const refreshToken =
          hashParams.get('refresh_token') ?? urlParams.get('refresh_token');
        // Capture the cross-WebView entry context BEFORE we strip params.
        // `from=core` is set by Core's goToFitting when it opens Fitting
        // inside SFSafariViewController; the bottom-nav "My Bag"
        // affordance reads this from sessionStorage and suppresses
        // itself in that context (otherwise tapping it loads Core *on
        // top* of the parent Core app underneath the popover).
        const from = hashParams.get('from') ?? urlParams.get('from');
        if (from) {
          try {
            sessionStorage.setItem('golfauth_arrived_via', from);
          } catch {
            /* sessionStorage can be unavailable in private-mode iOS Safari */
          }
        }
        if (!token) {
          // Even without a token, strip any residual auth-shaped
          // parameters from BOTH query + fragment so they don't
          // linger in browser history or leak via a shared URL.
          // Previously the whole cleanup block was gated on `from`
          // being present — meaning a stale `#refresh_token=…` or
          // `#theme=dark` from an earlier navigation would survive
          // if the user landed here without `from`. Defensive.
          const url = new URL(window.location.href);
          const searchDirty = url.searchParams.has('from');
          url.searchParams.delete('from');
          const cleanedHash = new URLSearchParams(
            window.location.hash.replace(/^#/, ''),
          );
          const hashDirty =
            cleanedHash.has('from') ||
            cleanedHash.has('token') ||
            cleanedHash.has('refresh_token') ||
            cleanedHash.has('theme');
          cleanedHash.delete('from');
          cleanedHash.delete('token');
          cleanedHash.delete('refresh_token');
          cleanedHash.delete('theme');
          // Only touch history if anything actually changed — avoids
          // a redundant replaceState on the common clean-URL case.
          if (searchDirty || hashDirty) {
            const hashStr = cleanedHash.toString();
            window.history.replaceState(
              {},
              '',
              url.pathname + url.search + (hashStr ? '#' + hashStr : ''),
            );
          }
          return;
        }
        const { error } = await setSessionFromToken(supabase, token, refreshToken || undefined);
        if (error) console.error('Failed to restore session from token:', error);
        // Strip tokens from BOTH the query string and the fragment so
        // they don't survive in the browser history or a future share.
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        url.searchParams.delete('refresh_token');
        url.searchParams.delete('theme');
        url.searchParams.delete('from');
        window.history.replaceState({}, '', url.pathname + url.search);
      })();

// Connection test removed — it ran `select('count')` on the public
// `professionals` table on every page load. After the RLS column-grant
// lockdown (migration 20260508103810), anon can only read the directory
// columns and `count` is not among them, so PostgREST returns 401 and
// Lighthouse flags it as a console error. The Supabase client surfaces
// auth/network failures via its own onAuthStateChange / fetch errors,
// so no replacement is needed.

// Set up realtime subscription for customers table
supabase
  .channel('customers')
  .on(
    'postgres_changes',
    {
      event: '*',
      schema: 'public',
      table: 'customers'
    },
    () => {
      // Process changes silently without logging
    }
  )
  .subscribe();
